/**
 * Looking at a document, and since R19 editing it.
 *
 * Before this, clicking a document did nothing at all: you could add one,
 * count them and let them shape an answer, and never see what was in them.
 *
 * What it shows is the extracted text, because that is all the indexer keeps.
 * The original bytes are not stored anywhere, so a PDF reads back as words
 * rather than as a page and there is nothing to download. The screen says
 * that out loud. A reader that quietly showed something else, or offered a
 * download that could not work, would be worse than the blank it replaces.
 *
 * Full screen on a phone with a way back, an overlay panel on a desktop. Same
 * component either way, so a document opens the same from the project screen
 * and from the knowledge screen.
 *
 * R19, 26 augustus 2026: BEWERKEN LEEST VAN SCHIJF EN NIET WAT HIERBOVEN STAAT.
 * Dat is geen verfijning maar de kern. `/content` zet de tekst opnieuw in elkaar
 * uit de stukken; gemeten over 400 documenten wijkt die hersamenstelling bij 396
 * af van het bestand. Opslaan wat de viewer toont zou dus 99 procent van Franks
 * bestanden beschadigen. De bewerker haalt daarom `/source` op, en de hash die
 * daarbij komt gaat mee terug: wijkt het bestand daar bij het opslaan van af,
 * dan heeft iemand anders geschreven en gaat er niets doorheen.
 *
 * EN DE KNOP DRAAGT TEKST. Franks eis van 25 augustus, uit een geval dat hem
 * drie rondes kostte: de knop die hij nodig had was een naamloos icoon van 14
 * pixels naast een knop die iets anders deed. Een knop die in de code staat is
 * geen knop die een mens kan vinden.
 */

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, FileText, Loader2, Pencil } from "lucide-react";
import { API } from "@/lib/api";
import { alIngelezen, herindexTekst } from "@/lib/herindexmelding";
import { useTaal } from "@/contexts/TaalContext";

function refusalText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return fallback;
}

export default function DocumentViewer({ document: doc, onClose }) {
  const { t } = useTaal();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  // De bewerker staat LOS van de viewer hierboven, en dat is met opzet: zij
  // dragen verschillende tekst uit verschillende routes, en die twee mogen
  // nooit in elkaar overlopen.
  const [bron, setBron] = useState(null);
  const [bewerken, setBewerken] = useState(false);
  const [tekst, setTekst] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const [opgeslagen, setOpgeslagen] = useState(null);
  // A, Franks klacht van 26 augustus 2026: "Ik sla op en zie niets veranderen."
  //
  // De viewer toont `/content`, en dat is de hersamenstelling uit de STUKKEN.
  // Die worden pas door de syncronde bijgewerkt - gemeten hoogstens vijftien
  // minuten later. Tot dan lijkt het alsof er niets is gebeurd. Deze state
  // houdt wat er ZOJUIST is opgeslagen, en het scherm laat DAT zien.
  //
  // BEWUST EEN EIGEN STATE EN NIET `state.data.content` OVERSCHREVEN: die komt
  // uit de indexeerweg en hoort niet te gaan liegen over zijn herkomst. Dit
  // veld weet dat het van schijf komt, en de regel eronder zegt dat ook.
  const [zojuistOpgeslagen, setZojuistOpgeslagen] = useState(null);
  const [onveranderd, setOnveranderd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!doc?.id) return undefined;
    setState({ loading: true, data: null, error: "" });
    setBewerken(false); setFout(""); setOpgeslagen(null); setBron(null);
    setZojuistOpgeslagen(null); setOnveranderd(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/knowledge/documents/${doc.id}/content`);
        if (!cancelled) setState({ loading: false, data: res.data, error: "" });
      } catch (error) {
        if (!cancelled) setState({ loading: false, data: null, error: refusalText(error, "Dit document kon niet worden geopend") });
      }
      try {
        // Meteen ophalen, want hierop hangt of er een bewerkknop MAG staan.
        // Een knop die bij het indrukken 403 geeft is erger dan geen knop.
        const res = await axios.get(`${API}/knowledge/documents/${doc.id}/source`);
        if (!cancelled) setBron(res.data);
      } catch {
        // Geen bronbestand is geen fout van dit scherm: een upload-bank heeft
        // er gewoon geen. Dan blijft het bij lezen.
        if (!cancelled) setBron({ writable: false, write_enabled: false, reason: "no-source" });
      }
    })();
    return () => { cancelled = true; };
  }, [doc?.id]);

  const haalBronOp = useCallback(async () => {
    const res = await axios.get(`${API}/knowledge/documents/${doc.id}/source`);
    setBron(res.data);
    setTekst(res.data.content ?? "");
    setFout("");
    return res.data;
  }, [doc?.id]);

  async function beginBewerken() {
    setOpgeslagen(null);
    setOnveranderd(false);
    try {
      await haalBronOp();
      setBewerken(true);
    } catch (error) {
      setFout(refusalText(error, "Het bestand kon niet van schijf worden gelezen"));
    }
  }

  async function bewaar() {
    setBezig(true); setFout("");
    try {
      const res = await axios.put(`${API}/knowledge/documents/${doc.id}/source`, {
        content: tekst,
        // DE HASH VAN WAT ER OP SCHIJF STOND TOEN WE HEM LAZEN. Zonder hem is
        // er geen manier om te zien of iemand anders ondertussen schreef.
        hash: bron?.hash,
      });
      setBewerken(false);
      setBron((vorige) => ({ ...(vorige || {}), hash: res.data.hash, content: tekst }));
      if (res.data.saved === false) {
        // C: er is niets misgegaan, er was alleen niets te doen. Nadrukkelijk
        // geen foutmelding; de route antwoordt hier 200.
        setOnveranderd(true);
        setOpgeslagen(null);
      } else {
        setOnveranderd(false);
        setOpgeslagen(res.data);
        setZojuistOpgeslagen(tekst);
      }
    } catch (error) {
      // DE TEKST VAN FRANK BLIJFT STAAN. Wie zijn werk kwijtraakt door een
      // foutmelding, vertrouwt de bewerker niet meer.
      setFout(refusalText(error, "Dit bestand kon niet worden opgeslagen"));
    } finally {
      setBezig(false);
    }
  }

  if (!doc) return null;
  const data = state.data;
  const failed = state.error || (data && data.status === "error" && data.error);
  const magBewerken = Boolean(bron?.writable && bron?.write_enabled);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background md:items-center md:justify-center md:bg-black/60 md:p-8">
      <div className="flex flex-col flex-1 min-h-0 w-full md:flex-none md:h-[80vh] md:max-w-3xl md:rounded-xl md:border md:bg-background md:shadow-xl overflow-hidden">
        <header className="flex items-center gap-2 px-3 border-b min-h-12 pt-[env(safe-area-inset-top)] md:pt-0 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            data-testid="document-viewer-close"
            aria-label={t("Terug")}
            className="tap-target flex items-center justify-center h-9 w-9 rounded text-muted-foreground flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <h2 className="truncate text-sm font-medium min-w-0" data-testid="document-viewer-title">{doc.filename}</h2>
          {!bewerken && magBewerken && (
            /* MET TEKST, en dat is de eis en geen smaak. Zie de kop. */
            <button
              type="button"
              onClick={beginBewerken}
              data-testid="document-edit-start"
              className="tap-target ml-auto flex items-center gap-1.5 h-9 px-3 rounded border text-sm font-medium flex-shrink-0"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              {t("Bestand bewerken")}
            </button>
          )}
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          {state.loading ? (
            <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : failed ? (
            <p className="text-sm text-destructive" data-testid="document-viewer-error">{failed}</p>
          ) : bewerken ? (
            <div className="flex flex-col gap-3">
              <p className="text-xs text-muted-foreground" data-testid="document-edit-note">
                {t("Dit is het bestand op schijf, niet de uitgepakte tekst hierboven. Opslaan overschrijft {f}, en dat kun je hier niet ongedaan maken.", { f: doc.filename })}
              </p>
              <textarea
                value={tekst}
                onChange={(e) => setTekst(e.target.value)}
                data-testid="document-editor"
                aria-label={`Bewerk ${doc.filename}`}
                spellCheck={false}
                className="w-full min-h-[50vh] rounded border bg-background p-3 text-sm font-mono"
              />
              {fout && (
                <div className="rounded border border-destructive/50 p-3 space-y-2">
                  <p className="text-sm text-destructive" data-testid="document-edit-error">{fout}</p>
                  {/* EEN MELDING MET EEN HANDELING ERIN. "Kon niet opslaan" is
                      een boodschap zonder uitweg; opnieuw inlezen is de enige
                      juiste volgende stap na een botsing. */}
                  <button
                    type="button"
                    onClick={() => haalBronOp().catch((e) => setFout(refusalText(e, "Het bestand kon niet worden gelezen")))}
                    data-testid="document-edit-reload"
                    className="tap-target h-9 px-3 rounded border text-sm font-medium"
                  >
                    {t("Opnieuw van schijf lezen")}
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={bewaar}
                  disabled={bezig}
                  data-testid="document-edit-save"
                  className="tap-target h-9 px-4 rounded bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
                >
                  {bezig ? t("Bezig met opslaan…") : t("Opslaan in bestand")}
                </button>
                <button
                  type="button"
                  onClick={() => { setBewerken(false); setFout(""); }}
                  data-testid="document-edit-cancel"
                  className="tap-target h-9 px-4 rounded border text-sm font-medium"
                >
                  {t("Annuleren")}
                </button>
              </div>
            </div>
          ) : !data?.content ? (
            <p className="text-sm text-muted-foreground" data-testid="document-viewer-empty">
              {t("Uit dit bestand kwam niets leesbaars, dus er is niets te tonen. Het telt nog wel als document, maar het kan geen vraag helpen beantwoorden.")}
            </p>
          ) : (
            <>
              {opgeslagen && (
                <p className="text-xs mb-3 rounded border p-2" data-testid="document-edit-saved">
                  {t("Opgeslagen in {f}.", { f: doc.filename })} {typeof opgeslagen.delta?.bytes === "number"
                    ? `${opgeslagen.delta.bytes >= 0 ? "+" : ""}${opgeslagen.delta.bytes} bytes.`
                    : ""}{" "}
                  <span data-testid="document-edit-index">{herindexTekst(opgeslagen.index, t)}</span>
                </p>
              )}
              {onveranderd && (
                /* C: GEEN foutkleur en geen foutmelding. Er is niets misgegaan;
                   er was alleen niets te doen. */
                <p className="text-xs text-muted-foreground mb-3 rounded border p-2"
                   data-testid="document-edit-unchanged">
                  {t("Niets op te slaan: {f} is ongewijzigd. Het bestand is niet aangeraakt, dus de tijdstempel is niet verschoven en Dropbox heeft niets bij te werken.", { f: doc.filename })}
                </p>
              )}
              <p className="text-xs text-muted-foreground mb-3" data-testid="document-viewer-note">
                {zojuistOpgeslagen !== null && alIngelezen(opgeslagen?.index)
                  ? t("Dit is het bestand zoals je het zojuist opsloeg, teruggelezen van wat er op schijf is geschreven. De geindexeerde tekst is er meteen opnieuw uit opgebouwd.")
                  : zojuistOpgeslagen !== null
                  ? t("Dit is het bestand zoals je het zojuist opsloeg, teruggelezen van wat er op schijf is geschreven. Het is niet de geindexeerde tekst; die wordt opnieuw opgebouwd als de map weer wordt ingelezen.")
                  : t("Dit is de tekst die uit het bestand is gehaald toen het werd toegevoegd. Het bestand zelf wordt niet bewaard, dus opmaak, afbeeldingen en indeling horen er niet bij.")}
              </p>
              {bron && !magBewerken && (
                /* WAAROM ER GEEN KNOP STAAT, en niet alleen DAT hij ontbreekt.
                   Een afwezige knop zonder uitleg laat iemand zoeken naar iets
                   wat er niet is. */
                <p className="text-xs text-muted-foreground mb-3" data-testid="document-edit-unavailable">
                  {bron.writable === false
                    ? t("Dit bestand kun je hier niet bewerken. Alleen markdownbestanden (.md) uit een gekoppelde map kunnen worden teruggeschreven; bij al het andere is wat er geindexeerd is de uitgepakte tekst, en die opslaan zou het origineel vervangen.")
                    : t("Bewerken staat uit. Zet “Wijzigingen terugschrijven naar het bestand” aan onder Instellingen › Meer om dit document te bewerken.")}
                </p>
              )}
              {/* A: na een geslaagde opslag staat hier wat er ZOJUIST is
                  opgeslagen, en niet de hersamenstelling uit de stukken van
                  hoogstens vijftien minuten geleden. Monospace, want dit is
                  het bestand en niet een uitgepakte tekst. */}
              <pre className={"whitespace-pre-wrap break-words text-sm "
                    + (zojuistOpgeslagen !== null ? "font-mono" : "font-sans")}
                   data-testid="document-viewer-text">
                {zojuistOpgeslagen !== null ? zojuistOpgeslagen : data.content}
              </pre>
              {data.truncated && (
                <p className="text-xs text-muted-foreground mt-4 pt-3 border-t" data-testid="document-viewer-truncated">
                  {t("Dit document is langer dan wat hier staat. De rest is geindexeerd en telt nog steeds mee als je er een vraag over stelt.")}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
